import React from 'react'
import { Button, ModalFooterAction } from '@apex/component-library'
import { WireTransferValues, BeneficiaryTypes, LocationOptions } from '../../containers/WireTransfers/Types'
import { SearchAccountsResponsePayload } from '@Types'
import {
  Box,
  AlertDialogCloseButton,
  AlertDialog,
  AlertDialogBody,
  Flex,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Text,
  Stack
} from '@chakra-ui/react'
import TransferDetailsSection from '../TransferPreviewModal/TransferDetailsSection'
import BeneficiarySection from './BeneficiarySection'
import BeneficiaryBankSection from './BeneficiaryBankSection'
import ModalTransferScheduledPreview from '../ModalTransferScheduledPreview'
import ModalDistributionDetailsPreview from '../TransferPreviewDetails/ModalDistributionDetailsPreview'
import ModalTransferFeesPreview from '../ModalComponents/ModalTransferFeesPreview'
import TransferAmountWarning from '../TransferAmountWarning'

export interface WireTransferPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  formikValues: WireTransferValues
  account: SearchAccountsResponsePayload
  onSubmit: () => void
  isSubmitting: boolean
  accountName: string
  fromAccount: string
  hasSubmitPermission: boolean
  amountAvailable: number
}

const WireTransferPreviewModal = ({
  isOpen,
  onClose,
  formikValues,
  onSubmit,
  isSubmitting,
  accountName,
  fromAccount,
  hasSubmitPermission,
  amountAvailable
}: WireTransferPreviewModalProps) => {
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const {
    transferAmountRaw,
    beneficiaryType,
    beneficiaryName,
    beneficiaryAccountNumOrIBAN,
    thirdPartyAddress,
    thirdPartyAddressLine2,
    thirdPartyCity,
    thirdPartyState,
    thirdPartyCountry,
    thirdPartyZip,
    bankName,
    totalBalanceCheckbox,
    bankRouting,
    bankCity,
    bankState,
    bankPostalCode,
    bankCountry,
    bankUseIntermediary,
    bankDestination,
    intermediaryName = '',
    intermediaryAccountNumber = '',
    intermediaryAddress,
    intermediaryAddressLine2,
    intermediaryCity,
    intermediaryState = '',
    intermediaryZip,
    intermediaryCountry,
    transferRecurrence,
    whenToSend,
    transferScheduledDate,
    transferStartDate,
    transferFrequency,
    numberOfTransfers,
    distributionReason,
    federalTaxWithholdingType,
    federalTaxWithholdingAmountRaw,
    stateTaxWithholdingType,
    stateTaxWithholdingAmountRaw,
    receivingInstitutionName,
    fees
  } = formikValues
  return (
    <AlertDialog
      motionPreset='slideInBottom'
      leastDestructiveRef={cancelRef}
      onClose={onClose}
      isOpen={isOpen}
      isCentered
    >
      <AlertDialogOverlay />
      <AlertDialogContent w='100%' maxW='600px'>
        <AlertDialogHeader as='h1' textStyle='h1'>
          Wire Withdrawal
        </AlertDialogHeader>
        <AlertDialogCloseButton />
        <AlertDialogBody>
          <Stack pb={8} spacing={8}>
            <Text>Please review the details of your transfer below.</Text>
            <Stack spacing={6}>
              <TransferDetailsSection
                fromAccount={fromAccount}
                transferAmount={transferAmountRaw}
                isTotalAmount={totalBalanceCheckbox}
                transferType='Wire'
              />
              <BeneficiarySection
                isThirdParty={beneficiaryType === BeneficiaryTypes.ThirdParty}
                beneficiaryName={beneficiaryName || accountName}
                bankAccountNumber={beneficiaryAccountNumOrIBAN}
                beneficiaryAddress={{
                  address: thirdPartyAddress,
                  addressLine2: thirdPartyAddressLine2,
                  city: thirdPartyCity,
                  stateProvince: thirdPartyState,
                  country: thirdPartyCountry,
                  postalCode: thirdPartyZip
                }}
              />
              <BeneficiaryBankSection
                bankName={bankName}
                bankSWIFTBIC={bankRouting}
                bankAddress={{
                  city: bankCity,
                  stateProvince: bankState,
                  postalCode: bankPostalCode,
                  country: bankCountry
                }}
                isIntermediaryAccount={bankUseIntermediary}
                intermediateBankAddress={{
                  name: intermediaryName,
                  accountNumber: intermediaryAccountNumber,
                  address: intermediaryAddress,
                  addressLine2: intermediaryAddressLine2,
                  city: intermediaryCity,
                  stateProvince: intermediaryState,
                  postalCode: intermediaryZip,
                  country: intermediaryCountry
                }}
                isInternationalBank={bankDestination === LocationOptions.International}
                isThirdParty={beneficiaryType === BeneficiaryTypes.ThirdParty}
              />
              <ModalTransferScheduledPreview
                transferRecurrence={transferRecurrence}
                whenToSend={whenToSend}
                transferScheduledDate={transferScheduledDate}
                transferStartDate={transferStartDate}
                transferFrequency={transferFrequency}
                numberOfTransfers={numberOfTransfers}
              />
              {distributionReason && (
                <ModalDistributionDetailsPreview
                  distributionReason={distributionReason}
                  federalTaxWithholdingType={federalTaxWithholdingType}
                  federalTaxWithholdingAmountRaw={federalTaxWithholdingAmountRaw}
                  stateTaxWithholdingType={stateTaxWithholdingType}
                  stateTaxWithholdingAmountRaw={stateTaxWithholdingAmountRaw}
                  receivingInstitutionName={receivingInstitutionName}
                />
              )}
              <ModalTransferFeesPreview fees={fees} />
            </Stack>
            <Text>Wires are submitted immediately for review, but not processed on weekends or banking holidays.</Text>
            <TransferAmountWarning amountAvailable={amountAvailable} transferAmount={transferAmountRaw} />
          </Stack>
        </AlertDialogBody>
        <ModalFooterAction>
          <Flex>
            <Box ml='auto'>
              <Button variant='white' ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant='primary'
                ml={3}
                onClick={onSubmit}
                isLoading={isSubmitting}
                isDisabled={!hasSubmitPermission || isSubmitting}
              >
                Confirm
              </Button>
            </Box>
          </Flex>
        </ModalFooterAction>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default WireTransferPreviewModal
